import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { companiesApi, deviceSpecsApi } from '../api'
import { getErrorMessage } from '../api/client'
import Toast from '../components/Toast'
import type { DeviceSpecDetail as SpecDetail, DeviceSpecDevice } from '../types'
import DeviceDrawer from './deviceSpec/DeviceDrawer'
import DeviceGrid from './deviceSpec/DeviceGrid'
import type { DeviceGridField, DeviceGridHandle } from './deviceSpec/DeviceGrid'
import EditMode from './deviceSpec/EditMode'
import type { EditStart } from './deviceSpec/EditMode'
import { useOutsideClose } from './deviceSpec/findScope'
import PullFromEdgeDialog from './deviceSpec/PullFromEdgeDialog'
import SaveBadge from './deviceSpec/SaveBadge'
import SpecSearch from './deviceSpec/SpecSearch'
import { NoteText } from './deviceSpec/SheetStatus'
import type { CellNote } from './deviceSpec/SheetStatus'
import MasterTab from './deviceSpec/MasterTab'
import type { ItemStart } from './deviceSpec/MasterEditMode'
import type { MachineGridHandle } from './deviceSpec/MachineGrid'
import type { IssueSpot } from './deviceSpec/model'
import { issueSpots, nextSpot, validateSpec } from './deviceSpec/model'
import type { SearchHit } from './deviceSpec/searchSpec'
import { duplicateDevice, moveDevice, useUndoKeys } from './deviceSpec/specOps'
import { PresenceNames, ViewerAvatars } from './deviceSpec/Presence'
import { hasAddress, useSpecDraft } from './deviceSpec/useSpecDraft'

// 디바이스 명세서 — 본문은 디바이스 시트(한 대 = 한 행, 엑셀처럼). 태그는 [편집 모드](태그 시트만 화면 가득, 탭 = 디바이스)에서
// 작성하고, 채널 상세(LiveEdge 등록 화면과 같은 칸)는 행의 [설정] 서랍에서 고친다. 두 화면 모두 실시간 공동 편집·자동 저장이고
// (useSpecDraft — 누가 어느 칸에 있는지 그 사람 색으로 보인다) Ctrl+Z / Ctrl+Y 는 내가 바꾼 것만 되돌린다.
// 처음 작성할 때는 디바이스 코드·프로토콜·채널 설정을 비워 두는 게 보통이라 필수가 아니다 —
// 그 값들은 Edge 로 전송할 때 채운다. [전송 전 점검]을 켜야 빠진 칸을 표시하고, [이전·다음 문제] 로 그 칸을 차례로 찾아간다.
// 짜임: 머리(이름·회사·메모, 저장 상태, 편집 모드, ⋯ 메뉴) → 탭 → 탭마다 같은 자리의 툴바(검색·되돌리기·개수 | 가져오기·탭 기능).
// 탭과 편집 모드는 주소에 둔다 (?tab=master, ?edit=tags|items) — 브라우저 뒤로 가기(Backspace·마우스 뒤로 버튼)가 명세 목록이 아니라
// 바로 전 화면(편집 모드 → 그 탭의 시트, 마스터데이터 탭 → 디바이스 탭)으로 가고, 새로고침·주소 복사로도 같은 화면이 열린다.
// 편집 모드 안의 디바이스·인터페이스 탭 이동은 기록에 쌓지 않는다 (Alt+↓ 로 수십 번 옮긴 만큼 뒤로 가기를 눌러야 하지 않게).
// 편집 모드를 열며 쌓은 기록 칸의 표시 (location.state) — 나가기가 그 칸을 되돌릴지(navigate(-1)) 주소만 덮을지 가른다
const EDIT_PUSHED = { editPushed: true }

export default function DeviceSpecDetail() {
  const { id = '' } = useParams<{ id: string }>()
  // 들어올 때 한 번 받아 바로 보여 주고, 공동 편집 연결(Join)이 되면 최신 원본(작성 중인 태그 포함)으로 바꾼다
  const { data: spec, error, isFetchedAfterMount } = useQuery({
    queryKey: ['device-specs', id],
    queryFn: () => deviceSpecsApi.get(id).then(r => r.data),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  })

  if (!id) return <Navigate to="/device-specs" replace />
  if (error) return <p className="text-sm text-red-600">{getErrorMessage(error, '명세서를 불러오지 못했습니다.')}</p>
  if (!spec || !isFetchedAfterMount) return <p className="text-sm text-gray-500">불러오는 중…</p>
  // 편집 중 캐시가 새로 받아져도 다시 그리지 않는다 (같은 key) — 이후 변경은 공동 편집 연결로 받는다
  return <SpecEditor key={spec.id} spec={spec} />
}

function SpecEditor({ spec: initial }: { spec: SpecDetail }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const spec = useSpecDraft(initial)
  const { draft, setDraft, locate, viewers, me } = spec
  const others = useMemo(() => viewers.filter(v => v.connectionId !== me), [viewers, me])
  const [panelKey, setPanelKey] = useState<string | null>(null)
  const [showIssues, setShowIssues] = useState(false)
  const [pullOpen, setPullOpen] = useState(false)
  const [note, setNote] = useState<CellNote | null>(null) // 디바이스 시트의 지금 칸이 붉은 이유
  const gridHandle = useRef<DeviceGridHandle | null>(null)
  const machineHandle = useRef<MachineGridHandle | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  useUndoKeys(spec.undo, spec.redo, spec.saveNow)

  // ── 탭 · 편집 모드 ↔ 주소 ──
  // 주소가 기준: ?tab=master 는 마스터데이터 탭, ?edit=tags 는 태그 편집, ?edit=items 는 항목 편집(마스터데이터 탭).
  // 열 칸(검색 결과의 행·칸)과 어느 디바이스·인터페이스인지는 상태로만 (주소엔 열림/닫힘만)
  const [params, setParams] = useSearchParams()
  const editParam = params.get('edit') // 'tags' | 'items' | null
  const tab: 'devices' | 'master' = params.get('tab') === 'master' || editParam === 'items' ? 'master' : 'devices'
  const [editStart, setEditStart] = useState<EditStart | null>(editParam === 'tags' ? { deviceKey: null } : null) // 있으면 태그 편집 모드
  const [masterEdit, setMasterEdit] = useState<string | null | undefined>(editParam === 'items' ? null : undefined) // 항목 편집 중인 인터페이스 (undefined = 닫힘)
  const [masterStart, setMasterStart] = useState<ItemStart | null>(null) // 항목 편집을 열 칸 (검색 결과)
  const editMode = editStart != null

  // 주소 → 상태: 뒤로·앞으로 가기, 주소로 바로 열기 (렌더 중에 맞춘다 — effect 로 따라가면 한 번 늦다).
  // 버튼으로 열고 닫을 때는 상태와 주소를 같이 바꾸므로 여기서는 할 일이 없다. 이 길로 열리면 첫 디바이스·인터페이스부터
  const [prevEdit, setPrevEdit] = useState(editParam)
  if (editParam !== prevEdit) {
    setPrevEdit(editParam)
    if (editParam === 'tags' && !editMode) setEditStart({ deviceKey: null })
    if (editParam !== 'tags' && editMode) setEditStart(null)
    if (editParam === 'items' && masterEdit === undefined) { setMasterStart(null); setMasterEdit(null) }
    if (editParam !== 'items' && masterEdit !== undefined) setMasterEdit(undefined)
  }

  // 주소 바꾸기 — 기본은 기록에 쌓는다(뒤로 가기로 돌아올 수 있게), replace 는 지금 기록을 덮는다
  const patchParams = (patch: { tab?: 'devices' | 'master'; edit?: 'tags' | 'items' | null }, options: { replace?: boolean; state?: unknown } = {}) =>
    setParams(prev => {
      const next = new URLSearchParams(prev)
      if (patch.tab !== undefined) { if (patch.tab === 'master') next.set('tab', 'master'); else next.delete('tab') }
      if (patch.edit !== undefined) { if (patch.edit) next.set('edit', patch.edit); else next.delete('edit') }
      return next
    }, options)

  const setTab = (next: 'devices' | 'master') => { if (next !== tab) patchParams({ tab: next }) }

  // 편집 모드 열기 — 상태를 놓고 주소에 edit 를 더한다(기록 한 칸, 그 칸에 editPushed 표시). 나가기는 그 한 칸을 되돌려
  // 기록을 더럽히지 않는다. 주소로 바로 들어왔거나 뒤로·앞으로로 열린 칸(표시 없음)이면 주소만 덮어쓴다
  // setParams 는 주소가 바뀔 때마다 새 함수라(지금 주소를 닫아 둔다) ref 로 최신 것을 쓴다 — openTagEdit 은 시트 열 정의에 들어가 바뀌지 않아야 한다
  const patchRef = useRef(patchParams)
  useEffect(() => { patchRef.current = patchParams })
  const openTagEdit = useCallback((start: EditStart) => {
    setEditStart(start)
    patchRef.current({ edit: 'tags' }, { state: EDIT_PUSHED })
  }, [])
  const openMasterEdit = (key: string | null, start: ItemStart | null = null) => {
    setMasterStart(start)
    setMasterEdit(key)
    patchParams({ tab: 'master', edit: 'items' }, { state: EDIT_PUSHED })
  }
  const closeEdit = () => {
    if ((location.state as { editPushed?: boolean } | null)?.editPushed) navigate(-1)
    else patchParams({ edit: null }, { replace: true })
  }

  // Ctrl+Shift+E — 지금 탭의 편집 모드 들어가기/나오기 (디바이스 → 태그 편집, 마스터데이터 → 항목 편집).
  // F2 는 엑셀처럼 칸 편집이라 쓰지 않는다
  const toggleEdit = () => {
    if (tab === 'master') { if (masterEdit !== undefined) closeEdit(); else openMasterEdit(machineHandle.current?.activeKey() ?? null) }
    else if (editMode) closeEdit()
    else openTagEdit({ deviceKey: gridHandle.current?.activeKey() ?? null })
  }
  const toggleEditRef = useRef(toggleEdit)
  useEffect(() => { toggleEditRef.current = toggleEdit })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'e') {
        e.preventDefault()
        toggleEditRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.getAll().then(r => r.data),
  })

  const issues = useMemo(() => validateSpec(draft.devices), [draft.devices])
  const tagCount = draft.devices.reduce((n, d) => n + d.tags.filter(hasAddress).length, 0) // 저장되는 태그만
  const panelIndex = draft.devices.findIndex(d => d.key === panelKey)
  const panelDevice = panelIndex >= 0 ? draft.devices[panelIndex] : null

  const setDevices = (update: (ds: DeviceSpecDevice[]) => DeviceSpecDevice[]) =>
    setDraft(d => ({ ...d, devices: update(d.devices) }))
  const changeDevice = (key: string, update: (d: DeviceSpecDevice) => DeviceSpecDevice) =>
    setDevices(ds => ds.map(d => (d.key === key ? update(d) : d)))

  const duplicate = (key: string) => {
    const { devices, copy } = duplicateDevice(draft.devices, key)
    setDevices(() => devices)
    if (copy) setPanelKey(copy.key)
  }

  const removeDevice = (key: string) => {
    const d = draft.devices.find(x => x.key === key)
    setDevices(ds => ds.filter(x => x.key !== key))
    setPanelKey(null)
    setToast({ message: `'${d?.name || '디바이스'}' 를 지웠습니다. (Ctrl+Z 로 되돌리기)`, type: 'success' })
  }

  const editTags = useCallback((key: string) => openTagEdit({ deviceKey: key }), [openTagEdit])
  const openPanel = useCallback((key: string) => setPanelKey(key), [])

  // [설정] 서랍이 열려 있는 동안 시트에서 커서를 뺀다 — 시트가 키 입력을 가져가 서랍 입력칸에 글자가 안 들어가므로. 닫으면 돌려놓는다
  const panelOpen = panelKey != null
  useEffect(() => {
    if (!panelOpen) return
    return gridHandle.current?.release()
  }, [panelOpen])

  // 내 자리 알리기 — 설정 서랍을 열면 그 디바이스 (시트 칸·태그 칸은 각 시트가 알린다)
  useEffect(() => {
    if (panelKey) locate({ mode: 'panel', device: panelKey })
    else if (!editMode) locate(null)
  }, [locate, panelKey, editMode])

  // 전송 전 점검 — [이전·다음 문제]: 디바이스 칸은 시트의 그 칸(접속 정보는 [설정] 서랍), 태그 칸은 편집 모드의 그 칸으로.
  // 처음엔 시트 커서 자리부터, 그다음은 방금 간 자리부터 (고친 칸은 목록에서 빠지므로 그대로 다음 칸이 나온다)
  const spots = useMemo(() => (showIssues ? issueSpots(draft.devices, issues) : []), [showIssues, draft.devices, issues])
  const lastSpot = useRef<IssueSpot | null>(null)
  useEffect(() => { lastSpot.current = null }, [showIssues])
  const goIssue = (delta: 1 | -1) => {
    const key = gridHandle.current?.activeKey()
    const index = key ? draft.devices.findIndex(d => d.key === key) : -1
    const at = lastSpot.current ?? (index >= 0 ? { deviceIndex: index, row: null, field: gridHandle.current?.activeField() ?? null } : null)
    const spot = nextSpot(spots, at, delta)
    if (!spot) return
    lastSpot.current = spot
    if (spot.row != null) openTagEdit({ deviceKey: spot.deviceKey, row: spot.row, field: spot.field })
    else if (spot.field === 'endpoint') setPanelKey(spot.deviceKey)
    else gridHandle.current?.focusDevice(spot.deviceKey, spot.field as DeviceGridField)
  }

  // 검색 결과로 — 태그 칸 → 편집 모드의 그 칸, 시트에 있는 디바이스 칸 → 그 칸, 시트에 없는 칸(메모·설명) → 설정 서랍,
  // 마스터데이터 항목 → 항목 편집의 그 칸, 인터페이스 칸 → 인터페이스 시트의 그 칸 (탭을 옮긴 뒤)
  const pickHit = (hit: SearchHit) => {
    if (hit.machine) {
      const { key, row, field } = hit.machine
      if (row != null) openMasterEdit(key, { row, field }) // 탭도 같이 옮긴다 (기록 한 칸)
      else {
        setTab('master')
        setTimeout(() => machineHandle.current?.focusMachine(key, field as 'code' | 'name' | 'description'), 60)
      }
      return
    }
    if (hit.row != null) openTagEdit({ deviceKey: hit.deviceKey, row: hit.row, field: hit.field })
    else if (hit.deviceField === 'memo' || hit.deviceField === 'description') setPanelKey(hit.deviceKey)
    else {
      setTab('devices')
      setTimeout(() => gridHandle.current?.focusDevice(hit.deviceKey, (hit.deviceField ?? 'name') as DeviceGridField), tab === 'devices' ? 0 : 60)
    }
  }

  const deleteSpec = async () => {
    if (!confirm(`명세서 '${draft.name}' 를 삭제할까요? 되돌릴 수 없습니다.${others.length ? `
지금 ${others.length}명이 함께 보고 있습니다.` : ''}`)) return
    try {
      await deviceSpecsApi.delete(initial.id)
      qc.invalidateQueries({ queryKey: ['device-specs'] })
      navigate('/device-specs', { replace: true })
    } catch (err) {
      setToast({ message: getErrorMessage(err, '삭제하지 못했습니다.'), type: 'error' })
    }
  }

  // 연결이 끊겼거나 거절됐거나 다른 사람이 지웠을 때
  const errorBar = spec.errorMessage && (
    <div className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
      {spec.errorMessage}
      {spec.deleted
        ? <Link to="/device-specs" className="underline font-medium">목록으로</Link>
        : <button onClick={spec.saveNow} className="underline font-medium">다시 연결</button>}
    </div>
  )

  // 두 탭 툴바의 왼쪽 — 검색·되돌리기 (편집 모드가 떠 있으면 검색은 뺀다: Ctrl+K 가 가려진 입력칸으로 가지 않게)
  const toolbarStart = (
    <>
      {!editMode && masterEdit === undefined && <SpecSearch devices={draft.devices} machines={draft.machines} onPick={pickHit} />}
      <div className="flex items-center gap-1">
        <button onClick={spec.undo} disabled={!spec.canUndo} title="되돌리기 (Ctrl+Z)"
          className="px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-30">↶ 되돌리기</button>
        <button onClick={spec.redo} disabled={!spec.canRedo} title="다시 실행 (Ctrl+Y)"
          className="px-2 py-1 rounded text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-30">↷ 다시 실행</button>
      </div>
    </>
  )

  return (
    <div className="flex flex-col h-full">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <PresenceNames viewers={others} />

      {editMode && (
        <EditMode spec={spec} start={editStart} issues={issues} showIssues={showIssues} onToggleIssues={() => setShowIssues(s => !s)}
          onClose={closeEdit} onNotice={(message, type = 'error') => setToast({ message, type })} />
      )}
      {pullOpen && (
        <PullFromEdgeDialog specDevices={draft.devices}
          onClose={() => setPullOpen(false)}
          onApply={(devices, summary) => {
            setDevices(() => devices)
            setPullOpen(false)
            setToast({ message: `${summary}. (Ctrl+Z 로 되돌리기)`, type: 'success' })
          }} />
      )}
      {editMode && errorBar && <div className="fixed bottom-14 left-1/2 -translate-x-1/2 z-50">{errorBar}</div>}

      <nav className="flex items-center gap-1.5 text-[13px] text-gray-500 mb-3 shrink-0">
        <Link to="/device-specs" className="hover:text-gray-700 transition">디바이스 명세</Link>
        <span>/</span>
        <span className="text-gray-800 truncate">{draft.name || '이름 없는 명세서'}</span>
      </nav>

      {/* 머리 — 이름·회사·메모 + 자동 저장 상태 + 지금 탭의 편집 모드 + ⋯ 메뉴 */}
      <div className="bg-surface rounded-xl border border-gray-200 px-5 py-4 shrink-0">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-[260px] space-y-2">
            <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="명세서 이름" aria-label="명세서 이름"
              className="w-full text-lg font-semibold tracking-tight text-gray-900 bg-transparent border border-transparent hover:border-gray-200 focus:border-blue-400 rounded px-1.5 -mx-1.5 outline-none" />
            <div className="flex flex-wrap items-center gap-2">
              <select value={draft.companyId} onChange={e => setDraft(d => ({ ...d, companyId: e.target.value }))} aria-label="회사"
                className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-700">
                <option value="">회사 미지정</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input value={draft.memo} onChange={e => setDraft(d => ({ ...d, memo: e.target.value }))} placeholder="메모" aria-label="메모"
                className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-2 py-1 text-xs" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ViewerAvatars viewers={viewers} me={me} />
            <SaveBadge status={spec.status} savedAt={spec.savedAt} error={spec.errorMessage} onRetry={spec.saveNow} />
            {/* 지금 탭의 편집 모드 — 디바이스: 태그 시트, 마스터데이터: 전송코드·태그 시트 */}
            {tab === 'devices' ? (
              <button onClick={() => openTagEdit({ deviceKey: gridHandle.current?.activeKey() ?? null })}
                title="태그를 전체 화면에서 편집합니다 (Ctrl+Shift+E)"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition">
                태그 편집 <span className="ml-1 text-[11px] text-white/80">Ctrl+Shift+E</span>
              </button>
            ) : (
              <button onClick={() => openMasterEdit(machineHandle.current?.activeKey() ?? null)} disabled={draft.machines.length === 0}
                title={draft.machines.length === 0 ? '인터페이스를 먼저 입력하세요' : '전송코드와 태그를 전체 화면에서 편집합니다 (Ctrl+Shift+E)'}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-40">
                항목 편집 <span className="ml-1 text-[11px] text-white/80">Ctrl+Shift+E</span>
              </button>
            )}
            <MoreMenu onDelete={deleteSpec} />
          </div>
        </div>

        {errorBar && <div className="mt-3">{errorBar}</div>}
      </div>

      {/* 탭 — 디바이스(태그) / 마스터데이터(설비·항목·키) */}
      <div role="tablist" className="flex items-end gap-1 mt-4 border-b border-gray-200 shrink-0">
        {([['devices', '디바이스', `${draft.devices.length}`], ['master', '마스터데이터', `${draft.machines.length}`]] as const).map(([key, label, count]) => (
          <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${tab === key ? 'border-accent text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:text-gray-800'}`}>
            {label} <span className="text-xs text-gray-500 font-normal">{count}</span>
          </button>
        ))}
      </div>

      {/* 디바이스 시트 / 마스터데이터 — 툴바는 스크롤해도 위에 남는다.
          위 여백은 스크롤 상자가 아니라 툴바(pt-3)에 둔다 — 상자에 padding-top 을 주면 sticky 가 그만큼 아래에 붙어 그 띠로 셀이 비친다 */}
      <div className="flex-1 min-h-0 overflow-y-auto pb-1 -mx-1 px-1">
        {tab === 'master' && (
          <MasterTab spec={spec} editKey={masterEdit} editStart={masterStart} onEdit={key => (key === undefined ? closeEdit() : openMasterEdit(key))} toolbarStart={toolbarStart}
            handleRef={h => { machineHandle.current = h }}
            onNotice={message => setToast({ message, type: 'success' })} onError={message => setToast({ message, type: 'error' })} />
        )}
        {tab === 'devices' && (
          <>
            <div className="sticky top-0 z-20 bg-canvas flex flex-wrap items-center gap-2 pt-3 pb-3">
              {toolbarStart}
              <span className="text-xs text-gray-500">디바이스 {draft.devices.length} · 태그 {tagCount}</span>
              <button onClick={() => setPullOpen(true)} title="Edge에 설정된 디바이스를 명세서로 가져옵니다"
                className="ml-auto border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50">Edge → 명세 가져오기</button>
              <div className="flex items-center">
                <button onClick={() => setShowIssues(s => !s)} title="Edge에 보내기 전에 채워야 할 칸을 표시합니다"
                  className={`px-3 py-1.5 text-xs transition ${showIssues && issues.count > 0 ? 'rounded-l-lg' : 'rounded-lg'} ${
                    !showIssues ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      : issues.count === 0 ? 'text-green-700 bg-green-50 ring-1 ring-green-200' : 'text-red-700 bg-red-50 ring-1 ring-red-200'
                  }`}>
                  {!showIssues ? '전송 전 점검' : issues.count === 0 ? '전송 준비 완료 · 끄기' : `문제 ${issues.count}곳 · 끄기`}
                </button>
                {showIssues && issues.count > 0 && (
                  <>
                    <button onClick={() => goIssue(-1)} title="이전 문제" aria-label="이전 문제"
                      className="px-2 py-1.5 text-xs text-red-700 bg-red-50 ring-1 ring-red-200 hover:bg-red-100">◀</button>
                    <button onClick={() => goIssue(1)} title="다음 문제 — 디바이스 칸은 시트에서, 태그 칸은 편집 모드에서 엽니다"
                      className="px-2.5 py-1.5 rounded-r-lg text-xs font-medium text-red-700 bg-red-50 ring-1 ring-red-200 hover:bg-red-100">다음 문제 ▶</button>
                  </>
                )}
              </div>
            </div>
            {!editMode && (
              <DeviceGrid devices={draft.devices} issues={issues} showIssues={showIssues} viewers={others}
                onLocate={(device, field, editing) => locate(device ? { mode: 'sheet', device, field, editing } : null)}
                onChange={devices => setDevices(() => devices)} onEditTags={editTags} onOpenPanel={openPanel}
                onNotice={message => setToast({ message, type: 'success' })} onNote={setNote}
                handleRef={h => { gridHandle.current = h }} />
            )}
            <div className="mt-2 px-1 text-xs min-h-4">
              <NoteText note={note} fallback="빈 행에 입력하면 디바이스가 추가됩니다. 엑셀에서 복사한 여러 행을 붙여넣을 수 있고, 우클릭으로 삽입, 복제, 삭제할 수 있습니다." />
            </div>
          </>
        )}
      </div>

      {/* 채널 설정 서랍 — 편집 모드의 [정보 수정] 과 같은 것 */}
      {panelDevice && (
        <DeviceDrawer device={panelDevice} index={panelIndex} count={draft.devices.length}
          issues={issues} showIssues={showIssues}
          onChange={update => changeDevice(panelDevice.key, update)}
          onMove={delta => setDevices(ds => moveDevice(ds, panelDevice.key, panelIndex + delta))}
          onDuplicate={() => duplicate(panelDevice.key)}
          onRemove={() => removeDevice(panelDevice.key)}
          onClose={() => setPanelKey(null)} />
      )}
    </div>
  )
}

// 머리 오른쪽 ⋯ — 자주 쓰지 않는 위험한 동작은 여기로 (두 탭 모두 같은 자리)
function MoreMenu({ onDelete }: { onDelete: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} title="더 보기" aria-label="더 보기" aria-expanded={open}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100">
        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor"><circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="13" cy="8" r="1.4" /></svg>
      </button>
      {open && <MoreMenuList onClose={() => setOpen(false)} onDelete={onDelete} />}
    </div>
  )
}

function MoreMenuList({ onClose, onDelete }: { onClose: () => void; onDelete: () => void }) {
  const ref = useOutsideClose(onClose)
  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-40 min-w-[160px] bg-surface border border-gray-200 rounded-lg shadow-lg py-1 text-sm">
      <button onClick={() => { onClose(); onDelete() }} className="block w-full text-left px-3 py-2 text-red-600 hover:bg-red-50">명세서 삭제…</button>
    </div>
  )
}
